import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PhotoIcon } from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import ImageUpload from '../components/ImageUpload';
import { addActivity } from '../services/activityService';
import apiService from '../services/apiService';

const NewsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image: '',
    date: new Date().toISOString().split('T')[0],
    category: 'მატჩი',
    published: true
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const loadArticle = async () => {
        try {
          const article = await apiService.getNewsById(id);
          if (article) {
            setFormData({
              title: article.title,
              excerpt: article.excerpt,
              content: article.content,
              image: article.image,
              date: article.date,
              category: article.category,
              published: article.published
            });
          }
        } catch (error) {
          console.error('Error loading article:', error);
        }
      };
      loadArticle();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await apiService.updateNews(id, formData);
        addActivity('news', 'updated', {
          title: formData.title,
          description: `ამბავი "${formData.title}" განახლდა`
        });
      } else {
        await apiService.createNews(formData);
        addActivity('news', 'created', {
          title: formData.title,
          description: `ახალი ამბავი "${formData.title}" დაემატა`
        });
      }
      navigate('/_panel-7c39f8/news');
    } catch (error) {
      console.error('Error saving news:', error);
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    'მატჩი',
    'ტრანსფერი',
    'აკადემია',
    'ღონისძიება',
    'სხვა'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader 
        title={isEdit ? 'ამბის რედაქტირება' : 'ახალი ამბავი'}
        subtitle={isEdit ? 'რედაქტირეთ ამბის ინფორმაცია' : 'შეავსეთ ახალი ამბის ინფორმაცია'}
      />

      {/* Form */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow sm:rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Title */}
              <div className="sm:col-span-2">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 font-georgian">
                  სათაური *
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-georgian"
                  placeholder="შეიყვანეთ ამბის სათაური"
                />
              </div>

              {/* Category and Date */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 font-georgian">
                  კატეგორია
                </label>
                <select
                  name="category"
                  id="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-georgian"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 font-georgian">
                  თარიღი
                </label>
                <input
                  type="date"
                  name="date"
                  id="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-georgian"
                />
              </div>

              {/* Image Upload */}
              <div className="sm:col-span-2">
                <ImageUpload
                  value={formData.image}
                  onChange={(url) => setFormData(prev => ({ ...prev, image: url }))}
                  category="news"
                  label="სურათი"
                  required={false}
                />
              </div>

              {/* Excerpt */}
              <div className="sm:col-span-2">
                <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 font-georgian">
                  მოკლე აღწერა *
                </label>
                <textarea
                  name="excerpt"
                  id="excerpt"
                  rows={3}
                  required
                  value={formData.excerpt}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-georgian"
                  placeholder="შეიყვანეთ ამბის მოკლე აღწერა"
                />
              </div>

              {/* Content */}
              <div className="sm:col-span-2">
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 font-georgian">
                  სრული ტექსტი *
                </label>
                <textarea
                  name="content"
                  id="content"
                  rows={10}
                  required
                  value={formData.content}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-georgian"
                  placeholder="შეიყვანეთ ამბის სრული ტექსტი"
                />
              </div>

              {/* Published */}
              <div className="sm:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="published"
                    id="published"
                    checked={formData.published}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="published" className="ml-2 block text-sm text-gray-900 font-georgian">
                    გამოქვეყნება
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/_panel-7c39f8/news')}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
              >
                გაუქმება
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-primary-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-georgian"
              >
                {saving ? 'შენახვა...' : 'შენახვა'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewsForm;
